import React, { useEffect, useState } from "react";
import "react-datepicker/dist/react-datepicker.css";
import { Upload, ArrowLeft, Check, IndianRupee } from "lucide-react";
import profileIcon from "../../../assets/profile_icon.svg";
import { WorkOrderApi } from "api/workorders/service";
import { convertToStringIndex } from "./ContractorDetails";

const AddPayment = ({ contractor, onClose }) => {
  const today = new Date().toISOString().split("T")[0];
  const [paymentDate, setPaymentDate] = useState(today);
  const [paidAmount, setPaidAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("Cash");
  const [remarks, setRemarks] = useState("");
  const [paymentType, setPaymentType] = useState("Schedule");
  const [workOrders, setWorkOrders] = useState([]);
  const [loadingWorkOrders, setLoadingWorkOrders] = useState(false);
  const [selectedWorkOrderId, setSelectedWorkOrderId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [isToday, setIsToday] = useState(true);

  useEffect(() => {
    if (!contractor?.id) return;
    let isCancelled = false;
    (async () => {
      try {
        setLoadingWorkOrders(true);
        const res = await WorkOrderApi.listByContractor(contractor.id);
        const list = Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res?.results)
          ? res.results
          : Array.isArray(res)
          ? res
          : [];
        if (!isCancelled) setWorkOrders(list || []);
      } catch (e) {
        if (!isCancelled) setWorkOrders([]);
      } finally {
        if (!isCancelled) setLoadingWorkOrders(false);
      }
    })();
    return () => {
      isCancelled = true;
    };
  }, [contractor?.id]);

  const submitPayment = async () => {
    if (!selectedWorkOrderId) {
      alert("Please select a work order");
      return;
    }
    const amountNum = Number(paidAmount);
    if (!amountNum || amountNum <= 0) {
      alert("Please enter a valid amount");
      return;
    }

    const methodMap = { Cash: "CASH", Online: "ONLINE", Cheque: "CHEQUE" };
    const typeMap = {
      Schedule: "SCHEDULE",
      Advance: "ADVANCE",
      "Extra/Bonus": "EXTRA/BONUS",
    };

    setSubmitting(true);
    try {
      await WorkOrderApi.updatePayment(selectedWorkOrderId, {
        paid_amount: amountNum,
        payment_type: typeMap[paymentType],
        payment_method: methodMap[paymentMethod],
        payment_date: paymentDate
      });
      onClose?.();
    } catch (e) {
      alert(e?.message || "Failed to save payment");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-[84%] h-[92vh] overflow-y-scroll mx-auto p-4 flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button onClick={onClose} className="text-[#232323] hover:text-black">
          <ArrowLeft className="w-5 h-5" />
        </button>

        <p className="ml-[450px] text-[#232323] text-[16px]">
          Contractor Payment
        </p>
      </div>

      <div className="flex justify-between gap-12 border rounded-[12px] border-gray-300 p-3 bg-white">
        {/* Left */}
        <div className="w-1/2 space-y-[30px]">
          <div className="flex items-center gap-3">
            <img src={profileIcon} alt="Vendor" className="w-10 h-10" />
            <div>
              <div className="text-[#232323] text-[16px]">
                {contractor?.name || "Contractor"}
                <span className="text-[12px] bg-gray-200 px-2 py-1 rounded-full ml-2 text-gray-600">
                  Contractor
                </span>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-[#000000] text-[14px] mb-1 font-medium">
              Select Work Order
            </label>
            <select
              className="w-full px-4 py-2 rounded-md bg-[#F3F4F6] cursor-pointer outline-none text-[#737373] font-medium text-sm"
              value={selectedWorkOrderId}
              onChange={(e) => setSelectedWorkOrderId(e.target.value)}
            >
              <option value="">
                {loadingWorkOrders
                  ? "Loading work orders..."
                  : "Work Order unassigned"}
              </option>
              {workOrders.map((wo) => (
                <option key={wo.id} value={wo.id}>
                  #WO_{wo?.site_name}_{convertToStringIndex(wo?.id)}
                </option>
              ))}
            </select>
          </div>

          <div className="mt-4">
            <label className="text-[14px] text-[#232323] mb-1 block">
              Select Payment Date
            </label>
            <div className="flex gap-3 items-center">
              <button
                onClick={() => {
                  setPaymentDate(today);
                  setIsToday(true);
                }}
                className={`border-1 border-[#7F599D] h-[36px] ${
                  isToday ? "bg-[#ECE6F0] text-[#3B0069]" : "bg-gray-50"
                }  rounded-[12px] px-5 py-1 text-sm font-medium`}
              >
                Now
              </button>
              <input
                type="date"
                value={paymentDate}
                onChange={(e) => {
                  setPaymentDate(e.target.value);
                  setIsToday(e.target.value === today);
                }}
                className="border rounded-lg px-4 py-2 text-[14px] text-[#232323] w-full bg-white"
              />
            </div>
          </div>

          <div className="pt-2">
            <p className="text-[#232323] text-[16px] mb-2">Payment</p>
            <hr className="mb-4" />
            <div className="flex items-center gap-4 mb-4">
              <label className="text-[#232323] text-[14px] whitespace-nowrap">
                Paid Amount
              </label>

              <div className="relative w-full">
                <IndianRupee className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 w-4 h-4" />
                <input
                  type="number"
                  placeholder="Amount"
                  className="pl-8 pr-4 py-2 border rounded-lg text-sm w-full bg-white"
                  value={paidAmount}
                  onChange={(e) => setPaidAmount(e.target.value)}
                />
              </div>
            </div>
            <div className="flex gap-2">
              <p className="text-[14px] text-[#232323]">Payment Method</p>
              {["Cash", "Online", "Cheque"].map((method) => (
                <button
                  key={method}
                  onClick={() => setPaymentMethod(method)}
                  className={`px-4 py-1 rounded-full text-sm font-medium ${
                    paymentMethod === method
                      ? "bg-[#ECE6F0] text-[#3B0069] border-1 border-[#3B0069]"
                      : "bg-gray-200 text-gray-700"
                  }`}
                >
                  {method}
                </button>
              ))}
            </div>

            <div className="flex gap-2 mt-4">
              <p className="text-[14px] text-[#232323]">Payment Type</p>
              {["Schedule", "Advance", "Extra/Bonus"].map((method) => (
                <button
                  key={method}
                  onClick={() => setPaymentType(method)}
                  className={`px-4 py-1 rounded-full text-sm font-medium ${
                    paymentType === method
                      ? "bg-[#ECE6F0] text-[#3B0069] border-1 border-[#3B0069]"
                      : "bg-gray-200 text-gray-700"
                  }`}
                >
                  {method}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Right */}
        <div className="w-1/2 space-y-4">
          <div>
            <label className="text-[#232323] text-[14px] mb-1 block">
              Remarks/ Comments (Optional)
            </label>
            <textarea
              type="text"
              placeholder="Type"
              className="w-full border rounded-lg px-4 py-2 text-sm min-h[60px] bg-white"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
            />
          </div>
          <div>
            <label className="text-[#232323] text-[14px] mb-1 block">
              Payment Slip (Optional)
            </label>
            <div className="w-full h-[36px] flex items-center justify-center px-4 py-8 bg-[#ECE6F0] rounded-lg text-sm text-[#3B0069] cursor-pointer hover:bg-gray-200">
              <span className="flex items-center gap-2">
                <Upload size={16} /> Upload Photos
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="mt-8 flex justify-center">
        <button
          onClick={submitPayment}
          disabled={submitting}
          className="bg-[#3B0069] disabled:opacity-70 disabled:cursor-not-allowed text-white px-16 py-2 rounded-[12px] font-medium hover:bg-purple-800 flex items-center gap-2"
        >
          Save
          <div className="w-6 h-6 rounded-full bg-[#5E2D91] flex items-center justify-center">
            <Check className="w-4 h-4 text-white" />
          </div>
        </button>
      </div>
    </div>
  );
};

export default AddPayment;
